import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  AlertTriangle,
  Search,
  List,
  Activity,
  Building2,
  Map as MapIcon,
  Table2,
  BarChart3,
  Target,
  FileBarChart,
  Menu,
  X,
  LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/clinics', icon: Building2, label: 'Clinics' },
  { to: '/cities', icon: Table2, label: 'Cities' },
  { to: '/map', icon: MapIcon, label: 'Map' },
  { to: '/events', icon: List, label: 'Events' },
  { to: '/ad-stats', icon: BarChart3, label: 'Ad Stats' },
  { to: '/targeting', icon: Target, label: 'Targeting' },
  { to: '/reports', icon: FileBarChart, label: 'Reports' },
  { to: '/calendar', icon: Calendar, label: 'Calendar' },
  { to: '/conflicts', icon: AlertTriangle, label: 'Conflicts' },
  { to: '/search', icon: Search, label: 'Search' },
];

function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { authRequired, logout } = useAuth();

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <div className="lg:hidden bg-dental-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-6 h-6 text-dental-400" />
          <div>
            <h1 className="text-lg font-bold">Dental Events</h1>
            <p className="text-dental-300 text-xs">Competitor Tracker</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-dental-700 rounded-lg transition-colors"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile overlay - between main and sidebar when menu open */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar - Desktop & Mobile */}
      <aside className={`
        lg:w-64 bg-gradient-to-b from-dental-900 via-dental-800 to-dental-900 text-white
        lg:relative fixed inset-0 z-50
        lg:translate-x-0 transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Desktop Header */}
        <div className="hidden lg:block p-6">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-dental-400" />
            <div>
              <h1 className="text-xl font-bold">Dental Events</h1>
              <p className="text-dental-300 text-xs">Competitor Tracker</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="mt-6 lg:mt-6 pt-4 lg:pt-0">
          {navItems.map(({ to, icon: Icon, label }) => {
            const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
            return (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive: active }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-dental-700/60 text-white border-r-4 border-accent-400'
                      : 'text-dental-200 hover:bg-dental-700/40 hover:text-white'
                  }`
                }
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            );
          })}
        </nav>

        {authRequired && (
          <div className="mt-4 pt-4 border-t border-dental-700">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-3 px-6 py-3 text-sm font-medium text-dental-200 hover:bg-dental-700 hover:text-white transition-colors w-full"
            >
              <LogOut className="w-5 h-5" />
              Log out
            </button>
          </div>
        )}

        {/* Pro Tip - Mobile: below nav; Desktop: bottom */}
        <div className="lg:hidden mt-4 px-6 pb-4">
          <div className="bg-dental-700 rounded-lg p-4">
            <p className="text-dental-300 text-xs mb-2">Pro Tip</p>
            <p className="text-sm text-dental-100">
              Check conflicts regularly to avoid scheduling your events on the same dates as competitors.
            </p>
          </div>
        </div>
        <div className="hidden lg:block absolute bottom-0 left-0 w-64 p-6">
          <div className="bg-dental-700 rounded-lg p-4">
            <p className="text-dental-300 text-xs mb-2">Pro Tip</p>
            <p className="text-sm text-dental-100">
              Check conflicts regularly to avoid scheduling your events on the same dates as competitors.
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

export default Layout;
